import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import {
  getAnalyticsSummary,
  listQRCodes,
  adminListUsers,
} from "../services/api";

const COLORS = [
  "#0046FF",
  "#00B8D9",
  "#1E9E5A",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#F97316",
  "#14B8A6",
  "#6366F1",
  "#84CC16",
];

const Svg = ({ size = 16, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const QrIcon = ({ size }) => (
  <Svg size={size}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <path d="M14 14h3v3h-3zM20 14v.01M14 20h3M20 17v4" />
  </Svg>
);

const ScanIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3" />
    <path d="M4 12h16" />
  </Svg>
);

const EyeIcon = ({ size = 18 }) => (
  <Svg size={size}>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </Svg>
);

const TrendIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M3 17l6-6 4 4 8-8" />
    <path d="M14 7h7v7" />
  </Svg>
);

const UsersIcon = ({ size }) => (
  <Svg size={size}>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </Svg>
);

function PieTooltip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;

  const row = payload[0].payload;
  const share = total ? Math.round((row.value / total) * 100) : 0;

  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{row.name}</div>

      <div className="chart-tooltip-row">
        <span
          className="chart-tooltip-dot"
          style={{ background: row.fill }}
        />
        <span className="chart-tooltip-name">Scans</span>
        <span className="chart-tooltip-value">{row.value}</span>
      </div>

      <div className="chart-tooltip-row">
        <span
          className="chart-tooltip-dot"
          style={{ background: "#9CA3AF" }}
        />
        <span className="chart-tooltip-name">Share of total</span>
        <span className="chart-tooltip-value">{share}%</span>
      </div>
    </div>
  );
}

const SortArrow = ({ column, sortKey, sortDir }) => {
  if (sortKey !== column) return null;

  return (
    <span className="sort-arrow">
      {sortDir === "asc" ? "↑" : "↓"}
    </span>
  );
};

const fmtDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

export default function AdminDashboard() {
  const navigate = useNavigate();

  const [summary, setSummary] = useState({
    total_qr_codes: 0,
    total_scans: 0,
  });

  const [qrCodes, setQrCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // null | 'qrcodes' | 'scans' | 'users'
  const [activeView, setActiveView] = useState(null);

  // Selected QR code for preview popup
  const [selectedQR, setSelectedQR] = useState(null);

  // Sorting state for the "All QR Codes" table.
  // sortKey: 'title' | 'created_at' | 'owner' | 'scan_count' | null
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      setSortKey(null);
      setSortDir("asc");
    }
  };

  useEffect(() => {
    Promise.all([
      getAnalyticsSummary(),
      listQRCodes(),
      adminListUsers(),
    ])
      .then(([summaryRes, qrRes, usersRes]) => {
        setSummary(summaryRes.data);
        setQrCodes(qrRes.data.results || qrRes.data || []);
        setUsers(usersRes.data.results || usersRes.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const totalUsers = summary.total_users ?? users.length;

  const totalQrCodes =
    summary.total_qr_codes ?? qrCodes.length;

  const totalScans =
    summary.total_scans ??
    qrCodes.reduce(
      (s, qr) => s + (qr.scan_count || 0),
      0
    );

  const avgScans = totalQrCodes
    ? totalScans / totalQrCodes
    : 0;

  const pieData = useMemo(
    () =>
      qrCodes.map((qr, i) => ({
        name: qr.title,
        value: qr.scan_count,
        fill: COLORS[i % COLORS.length],
      })),
    [qrCodes]
  );

  const qrCountByOwner = useMemo(() => {
    const map = {};

    qrCodes.forEach((qr) => {
      const owner = qr.owner_username || qr.owner;

      if (owner) {
        map[owner] = (map[owner] || 0) + 1;
      }
    });

    return map;
  }, [qrCodes]);

  const sortedQrCodes = useMemo(() => {
    if (!sortKey) return qrCodes;

    const dir = sortDir === "asc" ? 1 : -1;

    const getValue = (qr) => {
      switch (sortKey) {
        case "title":
          return (qr.title || "").toLowerCase();

        case "owner":
          return (
            qr.owner_username ||
            qr.owner ||
            ""
          ).toLowerCase();

        case "created_at":
          return qr.created_at
            ? new Date(qr.created_at).getTime()
            : 0;

        case "scan_count":
          return qr.scan_count || 0;

        default:
          return "";
      }
    };

    return [...qrCodes].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;

      return 0;
    });
  }, [qrCodes, sortKey, sortDir]);

  const toggleView = (view) => {
    setActiveView((v) =>
      v === view ? null : view
    );
  };

  const cardStyle = (view) => ({
    cursor: "pointer",

    ...(activeView === view
      ? {
          boxShadow:
            "0 0 0 3px rgba(0,70,255,0.25), 0 12px 32px rgba(16,24,40,0.12)",
        }
      : {}),
  });

  return (
    <div>
      <div className="page-title">
        Admin Dashboard
      </div>

      <div className="page-subtitle">
        Overview of QR codes and scan activity across all users
      </div>

      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(4, 1fr)",
          marginBottom: 24,
        }}
      >
        <div
          className="card stat-card stat-blue"
          style={cardStyle("qrcodes")}
          onClick={() => toggleView("qrcodes")}
        >
          <div className="stat-head">
            <QrIcon /> Total QR Codes
          </div>

          <div className="stat-value">
            {totalQrCodes}
          </div>

          <div className="stat-foot">
            Across all users
          </div>

          <div className="stat-watermark">
            <QrIcon size={96} />
          </div>
        </div>

        <div
          className="card stat-card stat-green"
          style={cardStyle("scans")}
          onClick={() => toggleView("scans")}
        >
          <div className="stat-head">
            <ScanIcon /> Total Scans
          </div>

          <div className="stat-value">
            {totalScans}
          </div>

          <div className="stat-foot">
            All time, all users
          </div>

          <div className="stat-watermark">
            <ScanIcon size={96} />
          </div>
        </div>

        <div
          className="card stat-card stat-amber"
          style={{ cursor: "pointer" }}
          onClick={() =>
            navigate("/admin/analytics")
          }
        >
          <div className="stat-head">
            <TrendIcon /> Avg. Scans per QR
          </div>

          <div className="stat-value">
            {avgScans.toFixed(1)}
          </div>

          <div className="stat-foot">
            Total scans ÷ QR codes
          </div>

          <div className="stat-watermark">
            <TrendIcon size={96} />
          </div>
        </div>

        <div
          className="card stat-card stat-purple"
          style={cardStyle("users")}
          onClick={() => toggleView("users")}
        >
          <div className="stat-head">
            <UsersIcon /> Total Users
          </div>

          <div className="stat-value">
            {totalUsers ?? "—"}
          </div>

          <div className="stat-foot">
            Registered accounts
          </div>

          <div className="stat-watermark">
            <UsersIcon size={96} />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <p>Loading...</p>
        </div>
      ) : activeView === "qrcodes" ? (
        <div className="card table-card stat-blue">
          <div className="card-title">
            <span className="card-title-icon">
              <QrIcon size={18} />
            </span>

            All QR Codes
          </div>

          {qrCodes.length === 0 ? (
            <p style={{ color: "#6B7280" }}>
              No QR codes have been created yet.
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th
                    className="sortable-th"
                    onClick={() => handleSort("title")}
                  >
                    Title{" "}
                    <SortArrow
                      column="title"
                      sortKey={sortKey}
                      sortDir={sortDir}
                    />
                  </th>

                  <th
                    className="sortable-th"
                    onClick={() =>
                      handleSort("created_at")
                    }
                  >
                    Created{" "}
                    <SortArrow
                      column="created_at"
                      sortKey={sortKey}
                      sortDir={sortDir}
                    />
                  </th>

                  <th
                    className="sortable-th"
                    onClick={() => handleSort("owner")}
                  >
                    Owner{" "}
                    <SortArrow
                      column="owner"
                      sortKey={sortKey}
                      sortDir={sortDir}
                    />
                  </th>

                  <th
                    className="sortable-th"
                    onClick={() =>
                      handleSort("scan_count")
                    }
                  >
                    Scans{" "}
                    <SortArrow
                      column="scan_count"
                      sortKey={sortKey}
                      sortDir={sortDir}
                    />
                  </th>

                  {/* NEW VIEW COLUMN */}
                  <th className="qr-view-header">
                    View
                  </th>
                </tr>
              </thead>

              <tbody>
                {sortedQrCodes.map((qr) => (
                  <tr key={qr.id}>
                    <td>{qr.title}</td>

                    <td className="cell-muted">
                      {fmtDateTime(qr.created_at)}
                    </td>

                    <td>
                      {qr.owner_username ||
                        qr.owner ||
                        "—"}
                    </td>

                    <td>
                      {qr.scan_count}
                    </td>

                    {/* NEW VIEW BUTTON */}
                    <td className="qr-view-cell">
                      <button
                        type="button"
                        className="qr-view-button"
                        onClick={() =>
                          setSelectedQR(qr)
                        }
                        title="View QR Code"
                        aria-label="View QR Code"
                      >
                        <EyeIcon size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : activeView === "scans" ? (
        <div className="card chart-card">
          <div className="card-title">
            <span className="card-title-icon">
              <ScanIcon size={18} />
            </span>

            Scans by QR Code
          </div>

          {pieData.length === 0 ? (
            <p style={{ color: "#6B7280" }}>
              No scan data yet.
            </p>
          ) : (
            <ResponsiveContainer
              width="100%"
              height={380}
            >
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={130}
                  label={false}
                  labelLine={false}
                >
                  {pieData.map((row) => (
                    <Cell
                      key={row.name}
                      fill={row.fill}
                    />
                  ))}
                </Pie>

                <Tooltip
                  content={
                    <PieTooltip
                      total={totalScans}
                    />
                  }
                />

                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      ) : activeView === "users" ? (
        <div className="card table-card stat-purple">
          <div className="card-title">
            <span className="card-title-icon">
              <UsersIcon size={18} />
            </span>

            All Users
          </div>

          {users.length === 0 ? (
            <p style={{ color: "#6B7280" }}>
              No users found.
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>QR Codes</th>
                </tr>
              </thead>

              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.username}</td>

                    <td>
                      {u.email || "—"}
                    </td>

                    <td>
                      {qrCountByOwner[u.username] ||
                        0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="card">
          <p
            style={{
              color: "#6B7280",
              margin: 0,
            }}
          >
            Click a stat card above to see the
            details.
          </p>
        </div>
      )}

      {/* ================================
          QR CODE VIEW POPUP
          Uses generated_image
         ================================= */}
      {selectedQR && (
        <div
          className="qr-view-modal-overlay"
          onClick={() => setSelectedQR(null)}
        >
          <div
            className="qr-view-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            <div className="qr-view-modal-header">
              <div>
                <div className="qr-view-modal-title">
                  {selectedQR.title}
                </div>

                <div className="qr-view-modal-subtitle">
                  QR Code Preview
                </div>
              </div>

              <button
                type="button"
                className="qr-view-modal-close"
                onClick={() =>
                  setSelectedQR(null)
                }
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="qr-view-modal-body">
              <div className="qr-popup-image-wrapper">
                <img
                  src={selectedQR.generated_image}
                  alt={`${selectedQR.title} QR Code`}
                  className="qr-popup-image"
                />
              </div>

              <div className="qr-popup-details">
                <div>
                  <span>Owner</span>

                  <strong>
                    {selectedQR.owner_username ||
                      selectedQR.owner ||
                      "—"}
                  </strong>
                </div>

                <div>
                  <span>Scans</span>

                  <strong>
                    {selectedQR.scan_count || 0}
                  </strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}